import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { EntitlementGate, EntitlementUsage } from '@features/entitlements';
import { useForms } from '@features/forms';
import ContactPage from '@mui/icons-material/ContactPage';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { FormWithRevisionSummary } from '@store/generated/forms';

import { useStickyHeaderDetection } from '../../hooks/useStickyHeaderDetection';
import type { Patient } from '../../types/types';
import SlideSidePane from '../../Views/Shared/SlideSidePane';

interface FormSelectorSlidePaneProps {
  open: boolean;
  onClose: () => void;
  patient?: Patient;
  subjectLanguage: string;
  subjectLanguageCap: string;
  selectedFormIds: string[];
  onFormSelect: (formId: string) => void;
  onCreateFormSession: () => void;
  onPreviewForm?: (formId: string) => void;
}

// Define the shape of form data we need
interface FormData {
  form_id: string;
  title: string;
  description: string;
  is_provider_form: boolean;
  is_org_form: boolean;
  type: 'public' | 'user' | 'group';
  form_type: 'standard' | 'test';
  estimated_time_minutes?: number;
}

interface FormGroupHeaderProps {
  id: string;
  title: string;
  isStuck: boolean;
  marginTop: number;
  registerSentinel: (id: string, element: HTMLElement | null) => void;
  registerHeader: (id: string, element: HTMLElement | null) => void;
}

const FormGroupHeader: React.FC<FormGroupHeaderProps> = ({
  id,
  title,
  isStuck,
  marginTop,
  registerSentinel,
  registerHeader,
}) => {
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        node.setAttribute('data-header-id', id);
        registerSentinel(id, node);
      }
    },
    [id, registerSentinel],
  );

  const headerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) registerHeader(id, node);
    },
    [id, registerHeader],
  );

  // Add padding-top when marginTop would have been applied
  const needsTopSpacing = marginTop > 0;

  return (
    <>
      <div ref={sentinelRef} style={{ height: 1 }} />
      <Typography
        ref={headerRef}
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          mb: 0,
          pt: needsTopSpacing ? 3 : 1,
          pb: 1,
          px: 1,
          position: isStuck ? 'sticky' : 'static',
          top: isStuck ? 0 : 'auto',
          backgroundColor: isStuck ? 'background.paper' : 'transparent',
          zIndex: isStuck ? 10 : 'auto',
          borderBottom: isStuck ? 1 : 0,
          borderColor: isStuck ? 'divider' : 'transparent',
          boxShadow: isStuck ? 'rgba(0, 0, 0, 0.1) 0px 2px 4px' : 'none',
          transition: 'all 0.2s ease',
        }}
      >
        {title}
      </Typography>
    </>
  );
};

// Transform V2 forms API response to legacy format for compatibility
function transformV2FormsToLegacy(v2Forms: FormWithRevisionSummary[]): FormData[] {
  return v2Forms.map(item => {
    const { form, current_revision } = item;

    // Determine form type based on owner_scope
    let type: 'public' | 'user' | 'group' = 'user';
    if (form.owner_scope === 'public') {
      type = 'public';
    } else if (form.owner_scope === 'org') {
      type = 'group';
    }

    // Type assertion for revision to access its properties
    const revision = current_revision as {
      title?: string;
      description?: string;
      estimated_time_minutes?: number;
    } | null;

    return {
      form_id: form.id || '',
      title: revision?.title || 'Untitled Form',
      description: revision?.description || '',
      is_provider_form: form.owner_scope === 'provider',
      is_org_form: form.owner_scope === 'org',
      type,
      form_type: 'standard' as const,
      estimated_time_minutes: revision?.estimated_time_minutes,
    };
  });
}

export const FormSelectorSlidePane: React.FC<FormSelectorSlidePaneProps> = ({
  open,
  onClose,
  patient,
  subjectLanguage,
  subjectLanguageCap,
  selectedFormIds,
  onFormSelect,
  onCreateFormSession,
  onPreviewForm,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
  const [formListPage, setFormListPage] = useState(1);
  const { isStuck, registerSentinel, registerHeader } = useStickyHeaderDetection();

  const FORMS_PER_PAGE = 30;

  const {
    forms: rawV2Forms,
    totalPages: totalFormPages,
    isLoading: _isLoadingForms,
  } = useForms(
    {
      page: formListPage,
      pageSize: FORMS_PER_PAGE,
      search: submittedSearchTerm || undefined,
    },
    {
      skip: !open,
    },
  );

  // Transform V2 forms to legacy format for compatibility
  const forms = transformV2FormsToLegacy(rawV2Forms);

  const availableForms = useMemo(() => {
    // Filter out forms that are already assigned to the patient
    type PatientWithForms = Patient & { forms?: Array<{ form_definition_id: string }> };
    const patientForms = (patient as PatientWithForms)?.forms;
    const alreadyAssignedIds = patientForms?.map(f => f.form_definition_id) || [];
    return forms.filter(form => !alreadyAssignedIds.includes(form.form_id));
  }, [forms, patient]);

  const groupedForms = useMemo(() => {
    const groups = {
      provider: [] as FormData[],
      org: [] as FormData[],
      public: [] as FormData[],
    };

    availableForms.forEach(form => {
      const formType = form.type;
      if (formType === 'user') {
        groups.provider.push(form);
      } else if (formType === 'group') {
        groups.org.push(form);
      } else {
        groups.public.push(form);
      }
    });

    return groups;
  }, [availableForms]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmittedSearchTerm(searchTerm);
    setFormListPage(1);
  };

  const handleFormListPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setFormListPage(page);
  };

  const handlePreviewForm = (formId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onPreviewForm) {
      onPreviewForm(formId);
    }
  };

  const renderFormCard = (form: FormData, isSelected: boolean) => {
    return (
      <>
        {isSelected && (
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              px: 1.5,
              py: 0.5,
              bgcolor: 'primary.soft',
              borderRadius: 2,
              border: 1,
              borderColor: 'primary.main',
            }}
          >
            <Typography variant="caption" color="primary" sx={{ fontWeight: "medium" }}>
              Selected
            </Typography>
          </Paper>
        )}

        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <ContactPage fontSize="small" color={isSelected ? 'primary' : 'action'} />
            <Typography variant="h6" color={isSelected ? 'primary.main' : 'text.primary'}>
              {form.title}
            </Typography>
          </Stack>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          }}
        >
          {form.description}
        </Typography>

        {/* Preview Link */}
        <Box>
          <Link
            component="button"
            variant="body2"
            onClick={e => {
              handlePreviewForm(form.form_id, e);
            }}
            underline="none"
            sx={{
              cursor: 'pointer',
              color: '#6453B5',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Preview
          </Link>
        </Box>
      </>
    );
  };

  return (
    <SlideSidePane open={open} onClose={onClose} title={`Select ${subjectLanguageCap} Forms`}>
      <EntitlementGate
        capabilityName="form_sending"
        source="form_selector_pane"
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              overflow: 'hidden',
            }}
          >
            <form onSubmit={handleSearchSubmit} style={{ marginBottom: 16 }}>
              <TextField
                fullWidth
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search forms..."
                variant="outlined"
                size="small"
               slotProps={{ input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" edge="end" aria-label="search forms">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                } }}/>
            </form>

            <Box
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                pb: 2,
                position: 'relative',
              }}
            >
              {availableForms.length > 0 ? (
                <>
                  {/* My Forms Section */}
                  {groupedForms.provider.length > 0 && (
                    <Box>
                      <FormGroupHeader
                        id="provider"
                        title="My Forms"
                        isStuck={isStuck('provider')}
                        marginTop={0}
                        registerSentinel={
                          registerSentinel as (id: string, element: HTMLElement | null) => void
                        }
                        registerHeader={
                          registerHeader as (id: string, element: HTMLElement | null) => void
                        }
                      />
                      {groupedForms.provider.map((form, index) => {
                        const isSelected = selectedFormIds.includes(form.form_id);
                        return (
                          <Paper
                            key={form.form_id}
                            sx={{
                              p: 2,
                              mt: index === 0 ? 2 : 0,
                              mb: 2,
                              backgroundColor: isSelected ? 'primary.soft' : 'background.paper',
                              border: 1,
                              borderRadius: '0.75rem',
                              borderColor: isSelected ? 'primary.main' : 'borderColors.primary',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              position: 'relative',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.soft' : 'action.hover',
                                transform: 'translateY(-2px)',
                                boxShadow: 1,
                              },
                              '&:last-child': {
                                mb: 0,
                              },
                            }}
                            onClick={() => {
                              onFormSelect(form.form_id);
                            }}
                          >
                            {renderFormCard(form, isSelected)}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}

                  {/* Organization Forms Section */}
                  {groupedForms.org.length > 0 && (
                    <Box>
                      <FormGroupHeader
                        id="org"
                        title="Organization Forms"
                        isStuck={isStuck('org')}
                        marginTop={groupedForms.provider.length > 0 ? 24 : 0}
                        registerSentinel={
                          registerSentinel as (id: string, element: HTMLElement | null) => void
                        }
                        registerHeader={
                          registerHeader as (id: string, element: HTMLElement | null) => void
                        }
                      />
                      {groupedForms.org.map((form, index) => {
                        const isSelected = selectedFormIds.includes(form.form_id);
                        return (
                          <Paper
                            key={form.form_id}
                            sx={{
                              p: 2,
                              mt: index === 0 ? 2 : 0,
                              mb: 2,
                              backgroundColor: isSelected ? 'primary.soft' : 'background.paper',
                              border: 1,
                              borderRadius: '0.75rem',
                              borderColor: isSelected ? 'primary.main' : 'borderColors.primary',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              position: 'relative',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.soft' : 'action.hover',
                                transform: 'translateY(-2px)',
                                boxShadow: 1,
                              },
                            }}
                            onClick={() => {
                              onFormSelect(form.form_id);
                            }}
                          >
                            {renderFormCard(form, isSelected)}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}

                  {/* Available Forms Section */}
                  {groupedForms.public.length > 0 && (
                    <Box>
                      <FormGroupHeader
                        id="public"
                        title="Available Forms"
                        isStuck={isStuck('public')}
                        marginTop={
                          groupedForms.provider.length > 0 || groupedForms.org.length > 0 ? 24 : 0
                        }
                        registerSentinel={
                          registerSentinel as (id: string, element: HTMLElement | null) => void
                        }
                        registerHeader={
                          registerHeader as (id: string, element: HTMLElement | null) => void
                        }
                      />
                      {groupedForms.public.map((form, index) => {
                        const isSelected = selectedFormIds.includes(form.form_id);
                        return (
                          <Paper
                            key={form.form_id}
                            sx={{
                              p: 2,
                              mt: index === 0 ? 2 : 0,
                              mb: 2,
                              backgroundColor: isSelected ? 'primary.soft' : 'background.paper',
                              border: 1,
                              borderRadius: '0.75rem',
                              borderColor: isSelected ? 'primary.main' : 'borderColors.primary',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              position: 'relative',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.soft' : 'action.hover',
                                transform: 'translateY(-2px)',
                                boxShadow: 1,
                              },
                            }}
                            onClick={() => {
                              onFormSelect(form.form_id);
                            }}
                          >
                            {renderFormCard(form, isSelected)}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </>
              ) : (
                <Typography color="text.secondary" align="center">
                  No forms found. Try adjusting your search.
                </Typography>
              )}

              {/* Show "Create your own" message on last page or when no results */}
              {(formListPage === totalFormPages || availableForms.length === 0) && (
                <Box sx={{ mt: 5, mb: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Could not find the {subjectLanguage} form you wanted?{' '}
                    <Link component={RouterLink} to="/forms" underline="always">
                      Create your own
                    </Link>
                  </Typography>
                </Box>
              )}
            </Box>

            {availableForms.length > 0 && totalFormPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalFormPages}
                  page={formListPage}
                  onChange={handleFormListPageChange}
                  color="primary"
                />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
              mt: 'auto',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
            }}
          >
            <Stack spacing={2}>
              <Button
                variant="contained"
                disabled={selectedFormIds.length === 0}
                onClick={onCreateFormSession}
                fullWidth
                size="large"
                sx={{
                  fontWeight: 'medium',
                  height: 48,
                  borderRadius: '0.5rem',
                  textTransform: 'none',
                  color: 'white',
                }}
              >
                Select {subjectLanguageCap} Forms to Send ({selectedFormIds.length})
              </Button>
              <EntitlementUsage
                capabilityName="form_sending"
                source="form_selector_pane"
                sx={{ mt: 2 }}
              />
            </Stack>
          </Box>
        </Box>
      </EntitlementGate>
    </SlideSidePane>
  );
};

export default FormSelectorSlidePane;
